<template>
  <v-card
    id="bulkInvitesContent"
    class="max-shadow"
  >
    <v-toolbar
      :class="{ 'elevation-2': scrolled}"
      height="64"
      color="white"
      flat
    >
      <v-btn
        icon
        @click="navigateBack"
      >
        <v-icon color="iconColor">
          arrow_back
        </v-icon>
      </v-btn>
      <v-toolbar-title>
        Bulk Invite
      </v-toolbar-title>
      <v-spacer />
      <v-toolbar-items>
        <v-btn
          color="primary"
          flat
          href="SampleInvites.csv"
        >
          <v-icon>get_app</v-icon>&nbsp;Sample.CSV
        </v-btn>
        <v-btn
          :disabled="importedVisitors.length === 0"
          color="primary"
          flat
          @click="_saveInvites"
        >
          invite
        </v-btn>
      </v-toolbar-items>
    </v-toolbar>
    <div v-scroll:#bulkInvitesContentWOToolbar="onScroll">
      <v-form ref="form">
        <v-layout
          id="bulkInvitesContentWOToolbar"
          row
          wrap
          class="pa-3"
        >
          <v-flex>
            <v-layout
              row
              wrap
            >
              <v-flex
                id="dropZone"
                xs12
                sm3
                class="drop-zone grey lighten-4"
                @mouseover="dropZoneHovered = !dropZoneHovered"
                @mouseout="dropZoneHovered = !dropZoneHovered"
                @dragenter.stop="dropZoneHovered = true"
                @drop.native="dropped"
                @click="$refs.fileInput.click()"
              >
                <v-icon
                  :size="72"
                  :class="dropZoneColor"
                >
                  description
                </v-icon>
                <div
                  :class="dropZoneColor"
                  class="subheading fw-500"
                >
                  {{ fileName }}
                </div>
                <input
                  ref="fileInput"
                  type="file"
                  accept=".csv"
                  hidden
                  @change="_fileSelected"
                >
              </v-flex>
              <v-flex sm9>
                <div class="px-4 grey--text text--darken-2 subheading">
                  <p>
                    Upload a CSV file to Invite Visitors.
                  </p>

                  <p><strong>Your CSV must:</strong></p>
                  <ul class="pl-5 wrap">
                    <li>
                      Must include header in the file. i.e first line in the CSV should be field names
                    </li>
                    <li>
                      <strong>List one visitor per line</strong>
                    </li>
                    <li>Contain a unique phone number for each visitor</li>
                    <li>
                      Match the following format:
                      <div class="format-sample my-2 pa-2 grey lighten-4">
                        <span>"Full Name"</span>,
                        <span>"Mobile"</span>,
                        <span>"Email"</span>,
                        <span>"Company"</span>,
                      </div>
                    </li>
                  </ul>
                  <p>
                    <strong>Note:</strong> Email and company are optional.
                  </p>
                </div>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-layout
            v-if="importedVisitors.length !== 0"
            row
            wrap
            align-baseline
            class="pt-4 pb-2"
          >
            <v-flex
              xs12
              sm6
              class="pr-4"
            >
              <v-select
                v-model="selectedVisitorType"
                :items="visitorTypes"
                item-text="general.type_name"
                return-object
                label="Visitor Purpose"
              />
            </v-flex>
            <v-flex
              xs12
              sm6
              class="pl-4"
            >
              <v-input
                v-model="selectedDateTime"
                :rules="[ (v) => !!v || 'Expected date and time is required']"
                class="inviteDateTimePicker"
              >
                <template slot="default">
                  <date-time-picker
                    v-model="selectedDateTime"
                    :show-placeholder="true"
                    :min="minDateTime"
                    :allowed-minute-steps="5"
                    title="Expected At"
                  />
                </template>
              </v-input>
            </v-flex>
            <v-flex
              xs12
              sm6
              class="pr-4"
            >
              <template v-if="selectedVisitorType.host_screen.host_selection">
                <vizmo-autocomplete
                  v-model="selectedHost"
                  :items="employees"
                  :rules="[v => (!!v && !!v.id) || 'Host is required']"
                  label="Host"
                  placeholder="Type to search Employees"
                />
              </template>
            </v-flex>
          </v-layout>
          <v-flex
            v-if="selectedVisitorType.general && selectedDateTime"
            xs12
            class="pt-3"
          >
            <div class="title fw-500">
              Invites Preview
            </div>
            <div class="body-2 py-2 grey--text text--darken-2">
              Please review and ensure that the below list contains all visitors and correct contact information
            </div>
            <v-data-table
              :headers="invitesHeaders"
              :items="importedVisitors"
              class="px-2 pb-2"
              item-key="id"
              no-data-text="Please select csv file for preview"
              hide-actions
              disable-initial-sort
            >
              <template
                slot="items"
                slot-scope="props"
              >
                <tr>
                  <td>{{ props.index + 1 }}</td>
                  <td class="text-xs-left">
                    {{ props.item.name }}
                  </td>
                  <td class="text-xs-left">
                    {{ props.item.phone }}
                  </td>
                  <td class="text-xs-left">
                    {{ props.item.email }}
                  </td>
                  <td class="text-xs-left">
                    {{ props.item.company }}
                  </td>
                  <td>
                    <v-edit-dialog
                      :return-value.sync="props.item.visitorType"
                      large
                      lazy
                      persistent
                      @save="updateHostSelection(props.item)"
                    >
                      <template v-if="props.item.visitorType">
                        {{ props.item.visitorType }}
                      </template>
                      <template v-else>
                        {{ selectedVisitorType.general.type_name }}
                      </template>
                      <v-icon
                        color="iconColor"
                        class="subheading"
                      >
                        edit
                      </v-icon>
                      <v-select
                        slot="input"
                        v-model="props.item.visitorType"
                        :items="visitorTypes"
                        item-text="general.type_name"
                        label="Visitor Purpose"
                      />
                    </v-edit-dialog>
                  </td>
                  <td>
                    <v-edit-dialog
                      :return-value.sync="props.item.host"
                      large
                      lazy
                      persistent
                    >
                      <template v-if="props.item.host">
                        {{ props.item.host.name }}
                      </template>
                      <template v-if="props.item.host">
                        <v-icon
                          color="iconColor"
                          class="subheading"
                        >
                          edit
                        </v-icon>
                        <template v-if="props.item.host">
                          <vizmo-autocomplete
                            slot="input"
                            v-model="props.item.host"
                            :items="employees"
                            :rules="[v => (!!v && !!v.id) || 'Host is required']"
                            label="Host"
                            placeholder="Type to search Employees"
                          />
                        </template>
                      </template>
                    </v-edit-dialog>
                  </td>
                  <td class="text-xs-left">
                    {{ formatExpectedAt(selectedDateTime) }}
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-flex>
        </v-layout>
      </v-form>
    </div>
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="0"
      :multi-line="snackbar.multiLine"
      bottom
    >
      {{ snackbar.message }}
      <v-btn
        flat
        @click.native="snackbar.show = false"
      >
        Close
      </v-btn>
    </v-snackbar>
  </v-card>
</template>
<script>
import Papa from 'papaparse';
import * as libphonenumber from 'libphonenumber-js';
import {
  some as _some,
  omit as _omit,
  get as _get,
  cloneDeep as _cloneDeep
} from 'lodash';
import * as moment from 'moment';

import dateTimePicker from '@/components/common/DateTimePicker';
import vizmoAutoComplete from '@/components/common/VizmoAutoComplete';

import { database } from '@/firebase';
import md5 from 'md5';

export default {
  name: 'BulkInvites',
  components: {
    'date-time-picker': dateTimePicker,
    'vizmo-autocomplete': vizmoAutoComplete
  },
  data() {
    return {
      fileName: 'Click to select',
      invites: [],
      importedVisitors: [],
      visitorTypes: [],
      selectedVisitorType: {
        host_screen: {}
      },
      selectedDateTime: '',
      minDateTime: '',
      createdBy: {},
      selectedHost: {},
      invitesHeaders: [
        {
          text: '#',
          value: false,
          align: 'left'
        },
        {
          text: 'Name',
          value: 'name',
          align: 'left'
        },
        {
          text: 'Phone',
          value: 'phone',
          align: 'left'
        },
        {
          text: 'Email',
          value: 'email',
          align: 'left'
        },
        {
          text: 'Company',
          value: 'company',
          align: 'left'
        },
        {
          text: 'Purpose',
          value: 'purpose',
          align: 'left'
        },
        {
          text: 'Host',
          value: 'host',
          align: 'left'
        },
        {
          text: 'Expected At',
          value: 'expectedAt',
          align: 'left'
        }
      ],
      dropZoneHovered: false,
      scrolled: false,
      offsetTop: 0,
      snackbar: {
        show: false,
        message: '',
        color: 'primary',
        multiLine: false
      },
      papa: {
        currentLine: 0,
        errors: []
      },
      saveButton: {
        loading: false
      },
      modifyVisitorType: false
    };
  },
  computed: {
    dropZoneColor() {
      return this.dropZoneHovered
        ? 'primary--text text--lighten-1'
        : 'grey--text text--darken-1';
    },

    locationId() {
      return _get(this.$store.state.VMS, 'lid');
    }
  },
  watch: {
    locationId: {
      handler(val) {
        if (!val) return;
        this.fetchVisitorTypes();
        this.fetchEmployees();
      },
      immediate: true
    },
    selectedVisitorType: {
      handler(val) {
        if (!val) return;
        // console.log(val);
        this.visitorTypeSelected();
      }
    },
    offsetTop: {
      handler(val) {
        if (val > 10) {
          this.scrolled = true;
        } else {
          this.scrolled = false;
        }
      },
      immediate: true
    }
  },
  created() {
    this.minDateTime = moment().format('YYYY-MM-DD HH:mm');
    // console.log(this.minTime);
  },
  methods: {
    navigateBack() {
      this.$router.back();
    },
    onScroll(e) {
      this.offsetTop = e.target.scrollTop;
    },
    dropped() {
      // console.log(e);
    },
    fetchVisitorTypes() {
      const path = `vms/${this.locationId}/settings/checkin_flow`;
      database.ref(path).once('value', visitorTypeSnapshot => {
        if (!visitorTypeSnapshot) return;
        this.componentLoading = false;
        const visitorTypes = visitorTypeSnapshot.val();
        const visitorTypesArray = this._toArray(visitorTypes);
        const filteredVisitorTypesArrayByEnabled = visitorTypesArray.filter(
          o => o.general.enabled
        );
        this.visitorTypes = filteredVisitorTypesArrayByEnabled;
      });
    },

    fetchEmployees() {
      const path = `vms/${this.locationId}/employees`;
      database.ref(path).once('value', employeeSnapshot => {
        const employees = employeeSnapshot.val();
        const employeesArray = this._toArray(employees);
        this.employees = employeesArray;
      });
    },

    _toArray(obj) {
      if (!obj) return [];
      return Object.keys(obj).map(key => {
        const item = obj[key];
        item.id = key;
        return item;
      });
    },

    visitorTypeSelected() {
      let host;
      if (this.$store.state.profile.role === 'employee') {
        const { profile } = this.$store.state;
        host = {
          name: profile.name,
          email: profile.email,
          phone: profile.phone || '',
          uid: profile.uid
        };
        this.selectedHost = host;
      } else {
        const hostEmpArray = this.employees.filter(
          obj => obj.email === this.$store.state.profile.email
        )[0];
        host = hostEmpArray;
      }
      const defaultHost = _omit(host, 'account', 'designation');
      this.createdBy = {
        name: defaultHost.name,
        email: defaultHost.email,
        phone: defaultHost.phone || null,
        department: defaultHost.department || null,
        designation: defaultHost.designation || null
      };
      this.selectedHost = defaultHost;
    },

    updateHostSelection(vis) {
      const visitorType = this.visitorTypes.filter(
        o => o.general.type_name === vis.visitorType
      )[0];
      if (visitorType.host_screen.host_selection) {
        // eslint-disable-next-line
        vis.host = {
          name: this.selectedHost.name,
          email: this.selectedHost.email,
          phone: this.selectedHost.phone,
          uid: this.selectedHost.id,
          department: this.selectedHost.department || null,
          designation: this.selectedHost.designation || null
        };
      }
    },
    _fileSelected() {
      // clear previous data
      this.importedVisitors = [];
      this.papa = {
        currentLine: 0,
        errors: []
      };

      const input = this.$refs.fileInput;
      if (input.files && input.files[0]) {
        const file = input.files[0];
        this.fileName = file.name;

        Papa.parse(file, {
          delimiter: '', // auto-detect
          newline: '', // auto-detect
          quoteChar: '"',
          header: true,
          step: this._papaStep.bind(this),
          complete: () => {
            if (this.papa.errors.length) {
              const csv = Papa.unparse(this.papa.errors, {
                quotes: true,
                quoteChar: '"',
                delimiter: ',',
                header: true
              });
              // download errors as a file
              const blob = new Blob([csv], {
                type: 'text/csv;charset=utf-8;'
              });

              const filename = 'employees_import_errors.csv';
              if (navigator.msSaveBlob) {
                // IE 10+
                navigator.msSaveBlob(blob, filename);
              } else {
                const link = document.createElement('a');
                if (link.download !== undefined) {
                  // feature detection
                  // Browsers that support HTML5 download attribute
                  const url = URL.createObjectURL(blob);
                  link.setAttribute('href', url);
                  try {
                    link.setAttribute('download', filename);
                  } catch (error) {
                    // eslint-disable-next-line
                    console.log(error);
                  }
                  link.style.visibility = 'hidden';

                  link.click();
                }
              }
              // console.log(this.importedVisitors);

              this.snackbar = {
                show: true,
                color: 'warning',
                multiLine: true,
                message:
                  'Some lines are skipped due to errors. Please check downloaded errors file for more information'
              };
            }
          }
        });
      }
    },

    _papaStep(results) {
      this.papa.currentLine += 1;
      const data = results.data[0];
      if (Object.keys(data).length !== 4) {
        this.papa.errors.push({
          'Line No': this.papa.currentLine,
          Error: 'Expected 5 columns but found 1'
        });
        return;
      }
      const vis = {
        name: data['Full Name'].trim(),
        phone: data.Mobile.trim(),
        email: data.Email.trim(),
        company: data.Company.trim(),
        host: null
      };
      vis.phone = this._validateAndFormatPhone(vis.phone);

      if (!this._validateName(vis.name)) {
        this.papa.errors.push({
          'Line No': this.papa.currentLine,
          Error: 'Invalid Visitor name'
        });
        return;
      }

      if (!this._validateAndFormatPhone(vis.phone)) {
        this.papa.errors.push({
          'Line No': this.papa.currentLine,
          Error: 'Invalid Employee email'
        });
        return;
      }

      if (
        _some(
          this.importedVisitors,
          o => o.email === vis.email || (!!vis.phone && o.phone === vis.phone)
        )
      ) {
        this.papa.errors.push({
          'Line No': this.papa.currentLine,
          Error: 'Duplicate visitor record'
        });
        return;
      }

      vis.id = md5(vis.phone);
      this.importedVisitors.push(vis);
    },
    formatExpectedAt(val) {
      return moment(val).format('DD-MM-YYYY hh:mm A');
    },
    _saveInvites() {
      if (!this.$refs.form.validate()) return;
      this.saveButton.loading = true;
      const importedVisitors = _cloneDeep(this.importedVisitors);
      const invites = [];
      let host;
      if (this.selectedVisitorType.host_screen.host_selection) {
        host = {
          name: this.selectedHost.name,
          email: this.selectedHost.email,
          phone: this.selectedHost.phone,
          uid: this.selectedHost.id,
          department: this.selectedHost.department || null,
          designation: this.selectedHost.designation || null
        };
      } else {
        const defaultHost = this.selectedVisitorType.host_screen
          .default_notificaiton_receiver;
        if (defaultHost) {
          host = {
            name: defaultHost.name,
            email: defaultHost.email,
            phone: defaultHost.phone,
            uid: defaultHost.id,
            department: defaultHost.department || null,
            designation: defaultHost.designation || null
          };
        } else {
          host = this.createdBy;
        }
      }
      importedVisitors.forEach(vis => {
        let visitorTypeKey = '';
        if (vis.visitorType) {
          visitorTypeKey = this.visitorTypes.filter(
            o => o.general.type_name === vis.visitorType
          )[0].id;
        } else {
          visitorTypeKey = this.visitorTypes.filter(
            o =>
              o.general.type_name === this.selectedVisitorType.general.type_name
          )[0].id;
        }
        const invite = {
          name: vis.name,
          email: vis.email,
          company: vis.company,
          phone: vis.phone,
          used: false,
          host: vis.host || host,
          createdBy: this.createdBy,
          createdAt: moment().toISOString(),
          expectedAt: moment(this.selectedDateTime).toISOString(),
          visitorType:
            vis.visitorType || this.selectedVisitorType.general.type_name,
          visitorTypeKey,
          comments: 'Added through bulk invite'
        };
        invites.push(invite);
      });
      const path = `vms/${this.locationId}/invites`;
      const inviteRefs = [];
      invites.forEach(invite => {
        const keyRef = database.ref(path).push();
        const ref = keyRef.set(invite);
        inviteRefs.push(ref);
      });
      Promise.all(inviteRefs).then(() => {
        this.saveButton.loading = false;
        this.navigateBack();
      });
    },

    arrayToObject(array) {
      return array.reduce((obj, item) => {
        const key = item.id;
        const value = _omit(item, ['id']);
        /* eslint-disable-next-line no-param-reassign*/
        obj[key] = value;
        return obj;
      }, {});
    },

    _validateName(name) {
      const nameRegex = /^[a-zA-Z ,.'-]{3,}$/;
      return nameRegex.test(name);
    },

    _validateEmail(email) {
      const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
      return emailRegex.test(email);
    },

    _validateAndFormatPhone(phone) {
      const parsed = libphonenumber.parse(phone, 'IN', { extended: true });
      return parsed.valid ? phone : '';
    }
  }
};
</script>
<style scoped>
.drop-zone {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  cursor: pointer;
  border-radius: 3px;
}

.format-sample {
  font-size: 14px;
  font-family: monospace;
  width: fit-content;
  font-weight: 600;
}
#bulkInvitesContent {
  height: calc(100vh - var(--toolbar-height));
}
#bulkInvitesContentWOToolbar {
  height: calc(100vh - (var(--toolbar-height) + 72px));
  overflow-y: scroll;
}
</style>

